// src/components/resources/tools/generators/shared/GeneratorExportBridge.tsx

'use client'

import Link from 'next/link'
import RiArrowRightLine from '~/icons/remix/generated/RiArrowRightLine'
import RiSparklingLine from '~/icons/remix/generated/RiSparklingLine'
import { siteConfig } from '~/lib/config/site'
import { getGeneratorGrowthCopy } from '~/lib/resources/generator-growth-config'
import { trackGeneratorBridgeClick } from './trackGeneratorBridge'
import {
  GENERATOR_GROWTH_COMPACT_CARD_CLASS,
  GENERATOR_GROWTH_CTA_CLASS,
  GENERATOR_GROWTH_ICON_TILE_CLASS,
} from './generatorGrowthStyles'

/**
 * Compact, high-intent bridge shown adjacent to the export/download control.
 * Surfaces the SaaS CTA at the moment users consider shipping creative.
 *
 * @param {Object} props - Component props.
 * @param {string} props.resourceSlug - Source generator slug for analytics.
 * @param {number} [props.engagementMs] - Time on tool before click (static snapshot).
 * @param {() => number} [props.getEngagementMs] - Engagement timer read at click time.
 * @returns {JSX.Element} Inline export bridge.
 */
export function GeneratorExportBridge({
  resourceSlug,
  engagementMs,
  getEngagementMs,
}: {
  resourceSlug: string
  engagementMs?: number
  getEngagementMs?: () => number
}): JSX.Element {
  const copy = getGeneratorGrowthCopy(resourceSlug)
  const destination = siteConfig.baseLinks.requestAccess

  return (
    <div className={`mt-4 ${GENERATOR_GROWTH_COMPACT_CARD_CLASS}`} role="note" aria-label="AdSights export hint">
      <div className="flex items-start gap-3">
        <div className={`${GENERATOR_GROWTH_ICON_TILE_CLASS} h-9 w-9 rounded-lg`}>
          <RiSparklingLine className="h-4 w-4" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-snug text-gray-900 dark:text-white">{copy.exportHintTitle}</p>
          <p className="mt-1 text-xs leading-relaxed text-gray-600 dark:text-gray-300">{copy.exportHintBody}</p>
        </div>
      </div>

      <Link
        href={destination}
        onClick={() => {
          trackGeneratorBridgeClick({
            resourceSlug,
            placement: 'export_hint',
            targetType: 'request_access',
            destinationPath: destination,
            engagementMs: getEngagementMs?.() ?? engagementMs,
          })
        }}
        className={`${GENERATOR_GROWTH_CTA_CLASS} mt-3`}
      >
        {copy.ctaLabel}
        <RiArrowRightLine className="h-3.5 w-3.5" aria-hidden />
      </Link>
    </div>
  )
}
